import React, { useState, useEffect } from "react";
import { FaSearch, FaShoppingCart, FaUserCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { getAllProducts, searchProducts } from "../services/productService";
import { IMAGE_BASE_URL } from "../api/api";
import logo from "../Assets/logo.svg";
import "../styles/Header.css";
import authService from "../services/authService";

function Headers({ user, onSignOut, isLoggedIn }) {
  const navigate = useNavigate();
  const { getCartItemsCount, clearCart } = useCart();
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  // Load all products on component mount for initial suggestions
  useEffect(() => {
    const loadAllProducts = async () => {
      try {
        const products = await getAllProducts();
        setAllProducts(products);
      } catch (error) {
        console.error("Failed to load products for search:", error);
      }
    };

    loadAllProducts();
  }, []);

  // Filter suggestions based on search query using loaded products
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const filtered = allProducts.filter(product =>
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.sub_category?.category?.name && 
       product.sub_category.category.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (product.sub_category?.name && 
       product.sub_category.name.toLowerCase().includes(searchQuery.toLowerCase()))
    ).slice(0, 8); // Limit to 8 suggestions
    
    setSuggestions(filtered);
    setShowSuggestions(filtered.length > 0);
  }, [searchQuery, allProducts]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      try {
        setLoading(true);
        // Navigate to search results page with the query
        navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
        setShowSuggestions(false);
        setSearchQuery(""); // Clear input after search
      } catch (error) {
        console.error("Search failed:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSuggestionClick = (product) => {
    // Navigate directly to product detail page
    navigate(`/product/${product.id}`);
    setShowSuggestions(false);
    setSearchQuery(""); // Clear search input
  };

  const handleSearchFocus = () => {
    if (suggestions.length > 0 && searchQuery.trim()) {
      setShowSuggestions(true);
    }
  };

  const handleSearchBlur = () => {
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  const handleProfileClick = () => {
    navigate("/User");
  };

  const handleAuthNavigate = () => {
    navigate("/Auth");
  };

  const handleCartClick = () => {
    navigate("/Cart");
  };

  const handleSignOutClick = async () => {
    try {
      setLogoutLoading(true);
      
      // Call the logout API
      await authService.logout();
      
      // Clear cart from context
      if (clearCart) {
        clearCart();
      }
      
      // Call the parent component's onSignOut callback if provided
      if (onSignOut) {
        onSignOut();
      }
      
      // Navigate to home page
      navigate("/");
      
    } catch (error) {
      console.error("Logout failed:", error);
      // Even if API call fails, clear local storage and state
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.setItem("userLogin", "False");
      
      // Clear cart even on logout failure
      if (clearCart) {
        clearCart();
      }
      
      if (onSignOut) {
        onSignOut();
      }
      navigate("/");
    } finally {
      setLogoutLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit(e);
    }
  };

  const getProductImage = (product) => {
    const primaryImage = product.images?.find((img) => img.is_primary);
    return primaryImage
      ? `${IMAGE_BASE_URL}storage/${primaryImage.image_path}`
      : "https://media.istockphoto.com/id/1147544807/vector/thumbnail-image-vector-graphic.jpg?s=612x612&w=0&k=20&c=rnCKVbdxqkjlcs3xH87-9gocETqpspHFXu5dIGB4wuM=";
  };

  const formatPrice = (product) => {
    return product.sale_price 
      ? `Rs.${product.sale_price}`
      : `Rs.${product.price}`;
  };

  // Determine if user is logged in (use prop or check localStorage as fallback)
  const userIsLoggedIn = isLoggedIn !== undefined ? isLoggedIn : authService.isLoggedIn();
  const currentUser = user || authService.getCurrentUser();

  return (
    <nav className="navbar navbar-light py-2 border-bottom">
      <div className="container d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center">
        
        {/* First Row: Logo + Cart + Auth/User */}
        <div className="d-flex w-100 justify-content-between align-items-center mb-2 mb-lg-0">
          {/* Logo */}
          <a className="navbar-brand d-flex align-items-center" href="/">
            <img src={logo} alt="Light Grocery Logo" width="60" height="60" />
          </a>

          {/* Search bar (desktop only) */}
          <div className="w-50 d-none d-lg-block position-relative">
            <div className="position-relative">
              <input
                className="form-control pe-5"
                type="search"
                placeholder="Search groceries..."
                aria-label="Search"
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                onKeyPress={handleKeyPress}
                disabled={loading}
              />
              <button 
                className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted border-0 bg-transparent"
                onClick={handleSearchSubmit}
                style={{ cursor: "pointer" }}
                disabled={loading}
              >
                {loading ? (
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  <FaSearch />
                )}
              </button>
            </div>

            {/* Search Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="search-suggestions">
                {suggestions.map((product) => (
                  <div
                    key={product.id}
                    className="suggestion-item"
                    onClick={() => handleSuggestionClick(product)}
                  >
                    <div className="suggestion-image">
                      <img 
                        src={getProductImage(product)} 
                        alt={product.name}
                        className="suggestion-img"
                      />
                    </div>
                    <div className="suggestion-content">
                      <div className="suggestion-name">{product.name}</div>
                      <div className="suggestion-category">
                        {product.sub_category?.category?.name || "Uncategorized"}
                      </div>
                      <div className="suggestion-price">{formatPrice(product)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: Cart + Sign In / Sign Up or User */}
          <div className="d-flex align-items-center">
            <div 
              className="position-relative d-flex align-items-center me-4"
              style={{ cursor: "pointer" }}
              onClick={handleCartClick}
            >
              <FaShoppingCart className="fs-5" />
              <span className="cart-badge">{getCartItemsCount()}</span>
            </div>

            {/* User authentication section */}
            {userIsLoggedIn && currentUser ? (
              <div className="d-flex align-items-center">
                <FaUserCircle
                  className="fs-3 text-dark me-2"
                  style={{ cursor: "pointer" }}
                  onClick={handleProfileClick}
                />
                <span className="me-2 d-none d-sm-block" style={{ fontSize: "0.9rem" }}>
                  Hi, {currentUser.full_name || currentUser.fullName}
                </span>
                <button 
                  className="btn btn-outline-secondary btn-sm" 
                  onClick={handleSignOutClick}
                  disabled={logoutLoading}
                >
                  {logoutLoading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-1" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Signing Out...
                    </>
                  ) : (
                    "Sign Out"
                  )}
                </button>
              </div>
            ) : (
              <div className="d-flex align-items-center">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm me-2"
                  style={{
                    border: "1px solid #6c757d",
                    color: "#343a40",
                    backgroundColor: "#fff",
                  }}
                  onClick={handleAuthNavigate}
                >
                  Sign In
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{ backgroundColor: "#FD7E14", color: "#fff", border: "none" }}
                  onClick={handleAuthNavigate}
                >
                  Sign Up
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Second Row: Search bar (mobile only) */}
        <div className="w-100 mt-2 d-block d-lg-none position-relative">
          <div className="position-relative">
            <input
              className="form-control pe-5"
              type="search"
              placeholder="Search groceries..."
              aria-label="Search"
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              onKeyPress={handleKeyPress}
              disabled={loading}
            />
            <button 
              className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted border-0 bg-transparent"
              onClick={handleSearchSubmit}
              style={{ cursor: "pointer" }}
              disabled={loading}
            >
              {loading ? (
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                <FaSearch />
              )}
            </button>
          </div>

          {/* Search Suggestions Dropdown for Mobile */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="search-suggestions mobile-suggestions">
              {suggestions.map((product) => (
                <div
                  key={product.id}
                  className="suggestion-item"
                  onClick={() => handleSuggestionClick(product)}
                >
                  <div className="suggestion-image">
                    <img 
                      src={getProductImage(product)} 
                      alt={product.name}
                      className="suggestion-img"
                    />
                  </div>
                  <div className="suggestion-content">
                    <div className="suggestion-name">{product.name}</div>
                    <div className="suggestion-category">
                      {product.sub_category?.category?.name || "Uncategorized"}
                    </div>
                    <div className="suggestion-price">{formatPrice(product)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </nav>
  );
}

export default Headers;